/**
 * AdminPage.js
 *
 * /fly/admin — the host's control room for Flight YJ030 (admin key, remembered on the device):
 * every guest's online check-in and arrival, guests' emails, the declarations as anonymous cards (CSV and printable,
 * for the Unclaimed Declarations wall), resetting arrivals, and sending email.
 * The host never sees who wrote which declaration or which statement is the lie: the backend only sends anonymous cards.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FlaskConical, Gamepad2, LogOut, Mail, Mic, MonitorPlay, Play, Printer, RefreshCw, RotateCcw, Send, Smartphone } from "lucide-react";
import toast from "react-hot-toast";
import KeyGate from "./components/KeyGate";
import { PrintedCard, cardLabel } from "./components/DeclarationCard";
import {
    fakeTrialDeclarations, fakeTrialReports, getAdminGame, getAdminGuests, getDeclarationCards, getVoiceMemos, resetArrivals,
    resetTrial, sendEmail, setGuestEmail, startTrial, voiceMemoUrl,
} from "./helpers/flyApi";
import { flyPath, formatTime, isTrial, useNoIndex, useStoredKey } from "./helpers/flyPage";

const PAPER = "#FBF7EF";
const INK = "#1B2A41";
const MUTED = "#5B6577";
const LINE = "#E2DCCF";
const ERROR = "#A8183D";
const OK = "#0D6B4F";
const MONO = "'JetBrains Mono', monospace";

// The guest table's groups, in order
const RSVP_GROUPS = [
    { rsvp: true,   title: "Coming" },
    { rsvp: false,  title: "Can't make it" },
    { rsvp: null,   title: "No reply" },
];
const WARN = "#B26A00";
const BACKDROP_TINT = "#EFE9DE";

const buttonClass = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.14em] transition hover:opacity-90 disabled:opacity-50";

/**
 * A short date and time in IST, e.g. '28 Oct, 21:04'
 */
const formatDateTime = (iso) => new Date(iso).toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

/**
 * Saves a file the browser has made
 */
const download = (filename, content, type) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

/**
 * The locked cards as a CSV, for printing the wall: card number, the three statements as printed, and the card's link.
 * No names, no lie.
 * @param {Array} cards     -   [{ number, statements, url }] from the backend
 */
const downloadCsv = (cards) => {
    const header = ["Card", "Statement 1", "Statement 2", "Statement 3", "URL"];
    const rows = cards.map((card) => [cardLabel(card.number), ...card.statements, card.url]);
    download("YJ030-declarations.csv", "\uFEFF" + [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n"), "text/csv;charset=utf-8");
};

/**
 * The same as JSON
 */
const downloadJson = (cards) => download(
    "YJ030-declarations.json",
    JSON.stringify(cards.map((card) => ({ card: cardLabel(card.number), statements: card.statements, url: card.url })), null, 2),
    "application/json",
);

/**
 * One file as { filename, content (base64), contentType }, for sendEmail
 */
const readAttachment = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ filename: file.name, content: reader.result.split(",")[1] || "", contentType: file.type || undefined });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});

/**
 * The printable Unclaimed Declarations wall: one card per guest, with its number, three statements and a QR code
 * to file a report on it. No name, no lie. The same look as the cards on guests' phones.
 * @param {Array} cards     -   [{ number, statements, url }] from the backend
 */
const DeclarationCards = ({ cards, onClose }) => {
    return (
        <div className="min-h-screen bg-white text-left" style={{ color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            <style>{"@media print { .no-print { display: none !important; } @page { margin: 12mm; } }"}</style>
            <div className="no-print flex gap-3 justify-center py-4">
                <button type="button" onClick={() => window.print()} className={buttonClass} style={{ background: INK, color: PAPER }}>
                    <Printer size={14} aria-hidden="true" /> PRINT
                </button>
                <button type="button" onClick={onClose} className={buttonClass} style={{ border: `2px solid ${INK}` }}>BACK</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 print:grid-cols-2" style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
                {cards.map((card) => <PrintedCard key={card.number} card={card} />)}
            </div>
            {cards.length === 0 && <p className="text-center py-10">No declarations yet.</p>}
        </div>
    );
};

/**
 * Whether a guest has opened their Wallet pass (Apple, Google or both), from the backend's record of them getting it
 */
const hasWalletPass = (guest) => !!(guest.applePassAt || guest.googlePassAt);

// Status colours: done, still to do, a problem (checked in without a Wallet pass), and doesn't apply yet
const TONES = {
    done    :   { background: `${OK}1a`, color: OK },
    todo    :   { background: `${WARN}1a`, color: WARN },
    problem :   { background: `${ERROR}1a`, color: ERROR },
    none    :   { background: "transparent", color: MUTED },
};

/**
 * A coloured label for one of a guest's steps
 * @param {String} tone     -   'done' | 'todo' | 'problem' | 'none'
 */
const Status = ({ tone, children }) => (
    <span className="inline-flex flex-col rounded-lg px-2.5 py-1 text-xs font-bold leading-snug" style={TONES[tone]}>{children}</span>
);

/**
 * Which Wallet pass the guest opened, and when. Red if they checked in but never opened one.
 */
const WalletCell = ({ guest }) => {
    if (!hasWalletPass(guest)) return guest.checkedInAt ? <Status tone="problem">Not opened</Status> : <Status tone="none">–</Status>;
    return (
        <Status tone="done">
            {guest.applePassAt && <span>Apple · {formatDateTime(guest.applePassAt)}</span>}
            {guest.googlePassAt && <span>Google · {formatDateTime(guest.googlePassAt)}</span>}
            {guest.walletPassOpens > 1 && <span className="font-medium opacity-80">Opened {guest.walletPassOpens} times</span>}
        </Status>
    );
};

/**
 * A guest's email: shown as text once there is one (with Edit), or a field to add it.
 * Saved when the field loses focus or Enter is pressed; saving it empty removes it.
 */
const EmailField = ({ guest, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(guest.email || "");
    useEffect(() => setValue(guest.email || ""), [guest.email]);
    const save = () => {
        setEditing(false);
        if (value.trim().toLowerCase() !== (guest.email || "")) onSave(guest, value.trim());
    };
    if (guest.email && !editing) {
        return (
            <span className="flex items-center gap-2">
                <span className="break-all">{guest.email}</span>
                <button type="button" onClick={() => setEditing(true)} className="text-xs underline underline-offset-2 shrink-0" style={{ color: MUTED }}>Edit</button>
            </span>
        );
    }
    return (
        <input
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            autoFocus={editing}
            placeholder="Add email"
            aria-label={`${guest.fullName}'s email`}
            className="w-full min-w-[180px] rounded border px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2"
            style={{ borderColor: LINE, "--tw-ring-color": INK }}
        />
    );
};

// The game's phases, and what admin calls them
const GAME_PHASES = {
    unlocked    :   "Cards lock when declarations close at 3:00 PM",
    locked      :   "Cards locked · guessing not open yet",
    open        :   "Guessing open",
    closed      :   "Final Descent · guessing closed",
    finished    :   "Reveal finished · guests see their results",
};

/**
 * Unclaimed Declarations, the guessing game: where it's at, and the two screens that run it. The cards lock by
 * themselves at 3:00 PM and guessing opens at 7:00 PM; the timer and the reveal are on the TV and the remote.
 */
const GamePanel = ({ adminKey }) => {
    const [game, setGame] = useState(null);

    const load = useCallback(async () => {
        try {
            setGame(await getAdminGame(adminKey));
        } catch (err) {
            toast.error(err.message || "Couldn't load the game.", { id: "game" });
        }
    }, [adminKey]);

    // Live: the counts keep up while guests file reports
    useEffect(() => {
        load();
        const timer = setInterval(load, 10000);
        return () => clearInterval(timer);
    }, [load]);

    const phase = game?.phase;
    return (
        <section className="rounded-2xl px-4 py-5 sm:px-6 flex flex-col gap-4" style={{ background: PAPER }}>
            <h2 className="flex items-center gap-2 font-bold tracking-[0.14em]"><Gamepad2 size={18} aria-hidden="true" /> UNCLAIMED DECLARATIONS</h2>
            {!game ? <p className="text-sm" style={{ color: MUTED }}>Loading…</p> : (
                <p className="text-sm">
                    {/* The trial has no fixed times: its game starts from the trial tools above */}
                    <b>{isTrial() && (phase === "unlocked" || phase === "locked") ? "Not started · press Start trial game above" : GAME_PHASES[phase]}</b>
                    {phase === "closed" && <b>{game.revealStarted ? ", reveal on the TV" : ", press Show the result to start the reveal"}</b>}
                    <span style={{ color: MUTED }}>
                        {" · "}{game.cards ? `${game.cards} cards` : `${game.declared} declarations from ${game.players} attending guests`}
                        {phase === "locked" && game.opensAt && ` · opens at ${formatTime(game.opensAt)}`}
                        {(phase === "locked" || phase === "open") && ` · closes at ${formatTime(game.closesAt)}`}
                        {phase !== "unlocked" && ` · ${game.reports} reports from ${game.officers} guests`}
                    </span>
                </p>
            )}
            {/* The TV and the host's phone remote, each in its own tab: the timer and the reveal are run from there */}
            <div className="flex flex-wrap gap-2">
                <a href={flyPath("/fly/custom-declaration-result")} target="_blank" rel="noreferrer" className={buttonClass} style={{ background: INK, color: PAPER }}>
                    <MonitorPlay size={14} aria-hidden="true" /> DISPLAY RESULT
                </a>
                <a href={flyPath("/fly/custom-declaration-result-remote")} target="_blank" rel="noreferrer" className={buttonClass} style={{ border: `2px solid ${INK}` }}>
                    <Smartphone size={14} aria-hidden="true" /> REMOTE
                </a>
            </div>
        </section>
    );
};

/**
 * The trial run's tools (/fly2/admin only): start it again from scratch, and fill it with made-up data to play alone
 */
const TrialTools = ({ adminKey, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const run = async (task, question, done) => {
        if (question && !window.confirm(question)) return;
        setBusy(true);
        try {
            toast.success(done(await task(adminKey)));
            onChanged();
        } catch (err) {
            toast.error(err.message || "Couldn't do that.");
        } finally {
            setBusy(false);
        }
    };
    return (
        <section className="rounded-2xl px-4 py-5 sm:px-6 flex flex-col gap-3" style={{ background: "#FFF6D6", border: "2px dashed #B26A00" }}>
            <h2 className="flex items-center gap-2 font-bold tracking-[0.14em]"><FlaskConical size={18} aria-hidden="true" /> TRIAL RUN</h2>
            <p className="text-sm" style={{ color: MUTED }}>
                Everything under /fly2 runs on trial data: the real guest list, with its own check-ins, declarations and game.
                Nothing here touches the real flight. Check-in never closes. Start trial game locks the cards and opens guessing for 30 minutes (fake declarations first, to play alone).
            </p>
            <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={() => run(startTrial, "Start the trial game? The cards lock and guessing opens for 30 minutes.", ({ minutes }) => `Trial game started: guessing open for ${minutes} minutes`)}
                    className={buttonClass} style={{ background: OK, color: PAPER }}><Play size={14} aria-hidden="true" /> START TRIAL GAME</button>
                <button type="button" disabled={busy} onClick={() => run(fakeTrialDeclarations, null, ({ declared }) => `Made up ${declared} declarations`)}
                    className={buttonClass} style={{ background: INK, color: PAPER }}>FAKE DECLARATIONS</button>
                <button type="button" disabled={busy} onClick={() => run(fakeTrialReports, null, ({ players }) => `${players} guests filed reports on every card`)}
                    className={buttonClass} style={{ background: INK, color: PAPER }}>FAKE REPORTS</button>
                <button type="button" disabled={busy} onClick={() => run(resetTrial, "Start the trial again from scratch? Every trial check-in, declaration and report is deleted.", () => "Trial reset")}
                    className={buttonClass} style={{ background: ERROR, color: PAPER }}><RotateCcw size={14} aria-hidden="true" /> RESET TRIAL</button>
            </div>
        </section>
    );
};

/**
 * One black box message: loaded only when played, so opening admin doesn't download every recording
 */
const VoiceMemoRow = ({ adminKey, memo }) => {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
    const load = async () => {
        setLoading(true);
        try {
            setUrl(await voiceMemoUrl(adminKey, memo.id));
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <li className="flex flex-wrap items-center gap-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
            <span className="text-2xl font-bold w-12" style={{ fontFamily: MONO }}>{String(memo.number).padStart(2, "0")}</span>
            {url
                ? <audio src={url} controls autoPlay className="flex-1 min-w-[220px]" />
                : (
                    <button type="button" onClick={load} disabled={loading} className={buttonClass} style={{ background: INK, color: PAPER }}>
                        <Play size={14} aria-hidden="true" /> {loading ? "LOADING…" : "PLAY"}
                    </button>
                )}
            <span className="text-xs" style={{ color: MUTED }}>{formatDateTime(memo.recordedAt)}</span>
        </li>
    );
};

/**
 * The black box: every guest's voice message, numbered and anonymous (no names)
 */
const VoiceMemos = ({ adminKey }) => {
    const [memos, setMemos] = useState(null);
    const load = useCallback(async () => {
        try {
            setMemos((await getVoiceMemos(adminKey)).memos);
        } catch (err) {
            toast.error(err.message || "Couldn't load the black box.");
        }
    }, [adminKey]);
    useEffect(() => { load(); }, [load]);
    return (
        <section className="rounded-2xl px-4 py-5 sm:px-6 flex flex-col gap-3" style={{ background: PAPER }}>
            <div className="flex items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-bold tracking-[0.14em]"><Mic size={18} aria-hidden="true" /> BLACK BOX · {memos ? memos.length : "…"} MESSAGES</h2>
                <button type="button" onClick={load} className={buttonClass} style={{ border: `2px solid ${INK}` }}><RefreshCw size={14} aria-hidden="true" /> REFRESH</button>
            </div>
            <p className="text-xs" style={{ color: MUTED }}>Anonymous and in a shuffled order. Guests were asked not to say their names.</p>
            {memos && memos.length === 0 && <p className="text-sm" style={{ color: MUTED }}>No messages yet.</p>}
            <ul>{memos?.map((memo) => <VoiceMemoRow key={memo.id} adminKey={adminKey} memo={memo} />)}</ul>
        </section>
    );
};

/**
 * Sends an email from the host's Gmail to picked guests and/or typed addresses
 */
const EmailForm = ({ adminKey, guests }) => {
    const [picked, setPicked] = useState([]);
    const [extra, setExtra] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    // { ok, message }
    const [outcome, setOutcome] = useState(null);

    const withEmail = guests.filter((guest) => guest.email);
    const toggle = (email) => setPicked(picked.includes(email) ? picked.filter((item) => item !== email) : [...picked, email]);
    const recipients = [...new Set([...picked, ...extra.split(/[\s,;]+/).map((item) => item.trim().toLowerCase()).filter(Boolean)])];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setOutcome(null);
        if (!recipients.length) return setOutcome({ ok: false, message: "Pick at least one guest or type an address." });
        if (!subject.trim()) return setOutcome({ ok: false, message: "Add a subject." });
        if (!body.trim()) return setOutcome({ ok: false, message: "Write the email." });
        setSending(true);
        try {
            const attachments = await Promise.all(files.map(readAttachment));
            const { sent, failed } = await sendEmail(adminKey, { to: recipients, subject, text: body, attachments });
            setOutcome({
                ok: failed.length === 0,
                message: `Sent to ${sent.length} ${sent.length === 1 ? "person" : "people"}.${failed.length ? ` Failed: ${failed.map((item) => item.to).join(", ")}.` : ""}`,
            });
        } catch (err) {
            setOutcome({ ok: false, message: err.message || "Couldn't send the email." });
        } finally {
            setSending(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="rounded-2xl px-4 py-5 sm:px-6 flex flex-col gap-4" style={{ background: PAPER }}>
            <h2 className="flex items-center gap-2 font-bold tracking-[0.14em]"><Mail size={18} aria-hidden="true" /> SEND EMAIL</h2>
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold tracking-[0.18em]" style={{ color: MUTED }}>GUESTS ({withEmail.length} WITH AN EMAIL)</span>
                    <button type="button" onClick={() => setPicked(picked.length === withEmail.length ? [] : withEmail.map((guest) => guest.email))} className="text-xs underline underline-offset-2">
                        {picked.length === withEmail.length && withEmail.length > 0 ? "Select none" : "Select all"}
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {withEmail.length === 0 && <p className="text-sm" style={{ color: MUTED }}>Add guests' emails in the table above.</p>}
                    {withEmail.map((guest) => (
                        <label key={guest.pnr} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm cursor-pointer" style={{ borderColor: picked.includes(guest.email) ? guest.colour : LINE }}>
                            <input type="checkbox" checked={picked.includes(guest.email)} onChange={() => toggle(guest.email)} style={{ accentColor: guest.colour }} />
                            {guest.fullName}
                        </label>
                    ))}
                </div>
            </div>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] font-bold tracking-[0.18em]" style={{ color: MUTED }}>OTHER ADDRESSES (COMMA-SEPARATED)</span>
                <input type="text" value={extra} onChange={(e) => setExtra(e.target.value)} className="rounded-lg border px-3 py-2 bg-white" style={{ borderColor: LINE }} />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] font-bold tracking-[0.18em]" style={{ color: MUTED }}>SUBJECT</span>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} className="rounded-lg border px-3 py-2 bg-white" style={{ borderColor: LINE }} />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] font-bold tracking-[0.18em]" style={{ color: MUTED }}>MESSAGE</span>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="rounded-lg border px-3 py-2 bg-white" style={{ borderColor: LINE }} />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] font-bold tracking-[0.18em]" style={{ color: MUTED }}>ATTACHMENTS (OPTIONAL, UP TO 4 MB)</span>
                <input type="file" multiple onChange={(e) => setFiles([...e.target.files].slice(0, 5))} className="text-sm" />
            </label>
            {outcome && <p role="status" className="text-sm font-medium" style={{ color: outcome.ok ? OK : ERROR }}>{outcome.message}</p>}
            <button type="submit" disabled={sending} className={`${buttonClass} self-start text-sm px-6 py-3`} style={{ background: INK, color: PAPER }}>
                <Send size={14} aria-hidden="true" /> {sending ? "SENDING…" : `SEND${recipients.length ? ` TO ${recipients.length}` : ""}`}
            </button>
            <p className="text-xs" style={{ color: MUTED }}>Each person gets their own copy, from your Gmail.</p>
        </form>
    );
};

const AdminPage = () => {
    const [key, saveKey, forgetKey] = useStoredKey("yj030-admin-key");
    const [keyError, setKeyError] = useState("");
    // { closed, guests } once loaded
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    // The anonymous cards, while the printable wall is open
    const [printing, setPrinting] = useState(null);
    // Only guests who checked in but haven't opened their Wallet pass
    const [onlyNoPass, setOnlyNoPass] = useState(false);
    // Bumped by the trial tools, so the game panel reloads
    const [gameVersion, setGameVersion] = useState(0);

    useNoIndex("Admin · Flight YJ030");

    const load = useCallback(async () => {
        if (!key) return;
        setLoading(true);
        try {
            setData(await getAdminGuests(key));
            setKeyError("");
        } catch (err) {
            if (err.status === 401) {
                forgetKey();
                setKeyError("That key didn't work.");
            } else {
                toast.error(err.message || "Couldn't load the guests.");
            }
        } finally {
            setLoading(false);
        }
    }, [key, forgetKey]);

    useEffect(() => { load(); }, [load]);

    const saveEmail = async (guest, email) => {
        try {
            const saved = await setGuestEmail(key, guest.pnr, email);
            setData((current) => ({ ...current, guests: current.guests.map((item) => (item.pnr === guest.pnr ? { ...item, email: saved.email } : item)) }));
            toast.success(saved.email ? `Saved ${guest.fullName}'s email` : `Removed ${guest.fullName}'s email`);
        } catch (err) {
            toast.error(err.message || "Couldn't save the email.");
        }
    };

    const reset = async () => {
        if (!window.confirm("Clear every arrival and reopen arrivals? Only for testing before the party.")) return;
        try {
            const { cleared } = await resetArrivals(key);
            toast.success(`Cleared ${cleared} ${cleared === 1 ? "arrival" : "arrivals"}`);
            load();
        } catch (err) {
            toast.error(err.message || "Couldn't reset arrivals.");
        }
    };

    const counts = useMemo(() => {
        const guests = data?.guests || [];
        return {
            total       :   guests.length,
            checkedIn   :   guests.filter((guest) => guest.checkedInAt).length,
            landed      :   guests.filter((guest) => guest.arrivedAt).length,
            coming      :   guests.filter((guest) => guest.rsvp === true).length,
            noPass      :   guests.filter((guest) => guest.checkedInAt && !hasWalletPass(guest)).length,
        };
    }, [data]);

    const shownGuests = (data?.guests || []).filter((guest) => !onlyNoPass || (guest.checkedInAt && !hasWalletPass(guest)));

    if (!key) {
        return (
            <div className="min-h-screen px-4" style={{ background: "#E9E4DA", color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                <KeyGate title="Admin" label="Admin key" error={keyError} onSubmit={(value) => { setKeyError(""); saveKey(value); }} />
            </div>
        );
    }

    // Exports and printing use the locked cards only, so nothing is printed with numbers that would change
    const withCards = async (use) => {
        try {
            const { locked, cards } = await getDeclarationCards(key);
            if (!locked) {
                toast.error("Lock the cards first (in Unclaimed Declarations below), so the numbers don't change after printing.");
                return;
            }
            use(cards);
        } catch (err) {
            toast.error(err.message || "Couldn't load the declarations.");
        }
    };

    if (printing) return <DeclarationCards cards={printing} onClose={() => setPrinting(null)} />;

    return (
        <div className="min-h-screen w-full pb-16 text-left" style={{ background: "#E9E4DA", color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            <div className="mx-auto w-full max-w-6xl px-4 pt-6 flex flex-col gap-5">
                <header className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-[11px] font-bold tracking-[0.24em]" style={{ color: MUTED }}>RIGHT BROTHER AIRWAYS · YJ030</p>
                        <h1 className="text-2xl font-bold tracking-[0.14em]">ADMIN{isTrial() && " · TRIAL RUN"}</h1>
                    </div>
                    <button type="button" onClick={forgetKey} className="inline-flex items-center gap-1 text-xs underline underline-offset-2" style={{ color: MUTED }}>
                        <LogOut size={12} aria-hidden="true" /> Lock
                    </button>
                </header>

                {!data ? (
                    <p className="text-center text-sm font-bold tracking-[0.24em] animate-pulse" style={{ color: MUTED }}>LOADING…</p>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            {[["Coming", counts.coming], ["Checked in online", counts.checkedIn], ["Landed", counts.landed], ["Arrivals", data.closed ? "Closed" : "Open"]].map(([label, value]) => (
                                <div key={label} className="rounded-xl px-4 py-3" style={{ background: PAPER }}>
                                    <p className="text-[11px] font-bold tracking-[0.16em] uppercase" style={{ color: MUTED }}>{label}</p>
                                    <p className="text-2xl font-bold" style={{ fontFamily: MONO }}>{value}{typeof value === "number" && <span className="text-sm opacity-60"> / {counts.total}</span>}</p>
                                </div>
                            ))}
                            {/* Who to chase: checked in, but never opened their Wallet pass. Click to see just them. */}
                            <button
                                type="button"
                                onClick={() => setOnlyNoPass(!onlyNoPass)}
                                aria-pressed={onlyNoPass}
                                className="rounded-xl px-4 py-3 text-left transition hover:opacity-90"
                                style={{ background: onlyNoPass ? ERROR : PAPER, color: onlyNoPass ? PAPER : INK, outline: counts.noPass && !onlyNoPass ? `2px solid ${ERROR}` : "none" }}
                            >
                                <p className="text-[11px] font-bold tracking-[0.16em] uppercase" style={{ color: onlyNoPass ? PAPER : counts.noPass ? ERROR : MUTED }}>Checked in, no Wallet pass</p>
                                <p className="text-2xl font-bold" style={{ fontFamily: MONO }}>{counts.noPass}<span className="text-sm opacity-60"> {onlyNoPass ? "· show all" : "· show"}</span></p>
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={load} disabled={loading} className={buttonClass} style={{ border: `2px solid ${INK}` }}>
                                <RefreshCw size={14} aria-hidden="true" /> REFRESH
                            </button>
                            <button type="button" onClick={() => withCards(downloadCsv)} className={buttonClass} style={{ background: INK, color: PAPER }}>
                                <Download size={14} aria-hidden="true" /> DECLARATIONS CSV
                            </button>
                            <button type="button" onClick={() => withCards(downloadJson)} className={buttonClass} style={{ background: INK, color: PAPER }}>
                                <Download size={14} aria-hidden="true" /> JSON
                            </button>
                            <button type="button" onClick={() => withCards(setPrinting)} className={buttonClass} style={{ background: INK, color: PAPER }}>
                                <Printer size={14} aria-hidden="true" /> PRINTABLE CARDS
                            </button>
                            <button type="button" onClick={reset} className={buttonClass} style={{ border: `2px solid ${ERROR}`, color: ERROR }}>
                                <RotateCcw size={14} aria-hidden="true" /> RESET ARRIVALS
                            </button>
                        </div>

                        <div className="rounded-2xl overflow-x-auto" style={{ background: PAPER }}>
                            <table className="w-full text-sm text-left">
                                <thead>
                                    <tr className="text-[11px] font-bold tracking-[0.14em] uppercase" style={{ color: MUTED, borderBottom: `1px solid ${LINE}` }}>
                                        <th className="px-3 py-3">Guest</th>
                                        <th className="px-3 py-3">Email</th>
                                        <th className="px-3 py-3">Started check-in</th>
                                        <th className="px-3 py-3">Online check-in</th>
                                        <th className="px-3 py-3">Declarations</th>
                                        <th className="px-3 py-3">Wallet pass</th>
                                        <th className="px-3 py-3">Arrived</th>
                                    </tr>
                                </thead>
                                {RSVP_GROUPS.map((group) => {
                                    const guests = shownGuests.filter((guest) => (guest.rsvp ?? null) === group.rsvp);
                                    if (guests.length === 0) return null;
                                    return (
                                        <tbody key={group.title}>
                                            <tr style={{ background: BACKDROP_TINT }}>
                                                <th colSpan={7} className="px-3 py-2 text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: INK }}>
                                                    {group.title} · {guests.length}
                                                </th>
                                            </tr>
                                            {guests.map((guest) => (
                                                <tr key={guest.pnr} className="align-top" style={{ borderBottom: `1px solid ${LINE}` }}>
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        <span className="flex items-center gap-2">
                                                            <span className="h-6 w-1.5 rounded-full" style={{ background: guest.colour }} aria-hidden="true" />
                                                            <span className="font-medium">{guest.fullName}</span>
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-3"><EmailField guest={guest} onSave={saveEmail} /></td>
                                                    {/* Got past "Find my booking" (from the backend; earlier check-ins count too) */}
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        {guest.checkInStartedAt ? <Status tone="done">{formatDateTime(guest.checkInStartedAt)}</Status> : <Status tone="todo">Not yet</Status>}
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        {guest.checkedInAt
                                                            ? <Status tone="done">{formatDateTime(guest.checkedInAt)}</Status>
                                                            : <Status tone="todo">{guest.checkInStartedAt ? "In progress" : "Not yet"}</Status>}
                                                    </td>
                                                    {/* Only whether they declared: what they wrote is for the wall, anonymously */}
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        {guest.declarationsSubmittedAt ? <Status tone="done">{formatDateTime(guest.declarationsSubmittedAt)}</Status> : <Status tone="todo">Not yet</Status>}
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap"><WalletCell guest={guest} /></td>
                                                    <td className="px-3 py-3 whitespace-nowrap" style={{ fontFamily: MONO }}>{guest.arrivedAt ? formatTime(guest.arrivedAt) : "–"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    );
                                })}
                            </table>
                        </div>

                        {isTrial() && <TrialTools adminKey={key} onChanged={() => { load(); setGameVersion((version) => version + 1); }} />}
                        <GamePanel key={gameVersion} adminKey={key} />
                        <VoiceMemos adminKey={key} />
                        <EmailForm adminKey={key} guests={data.guests} />
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
